import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { useEffect, useState } from "react";
import { Moon, Sun, LogOut } from "lucide-react";

export default function AppShell({ nav, brandLabel = "ExamNest", accent = "admin" }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));

  useEffect(() => {
    if (dark) document.documentElement.classList.add("dark");
    else document.documentElement.classList.remove("dark");
  }, [dark]);

  const initials = (user?.name || "U").split(" ").map(x => x[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div data-testid="app-shell" className="min-h-screen flex bg-background text-foreground">
      {/* sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-border bg-card/50">
        <div className="p-6 flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-primary text-primary-foreground grid place-items-center font-display font-bold">E</div>
          <div>
            <div className="font-display font-bold text-lg tracking-tight leading-none">{brandLabel}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-widest mt-0.5">{accent}</div>
          </div>
        </div>
        <nav className="flex-1 px-4 py-2 space-y-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
                  isActive ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:bg-muted hover:text-foreground"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 text-xs text-muted-foreground border-t border-border">v1.0 · ExamNest PWA</div>
      </aside>

      {/* main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 backdrop-blur-xl bg-background/70 border-b border-border">
          <div className="px-6 h-16 flex items-center justify-between gap-4">
            <div className="lg:hidden flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-primary text-primary-foreground grid place-items-center font-display font-bold">E</div>
              <span className="font-display font-semibold">{brandLabel}</span>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Button data-testid="toggle-theme" variant="ghost" size="icon" className="rounded-full" onClick={() => setDark(d => !d)}>
                {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="user-menu-btn" variant="ghost" className="rounded-full pl-2 pr-3 gap-2 h-10">
                    <Avatar className="h-8 w-8"><AvatarImage src={user?.avatar} /><AvatarFallback>{initials}</AvatarFallback></Avatar>
                    <div className="text-left hidden sm:block">
                      <div className="text-sm font-medium leading-none">{user?.name}</div>
                      <div className="text-xs text-muted-foreground mt-0.5 capitalize">{user?.role}</div>
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="logout-item" onClick={() => { logout(); navigate("/"); }}>
                    <LogOut className="h-4 w-4 mr-2" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          {/* mobile nav */}
          <div className="lg:hidden overflow-x-auto en-scroll border-t border-border">
            <div className="px-4 py-2 flex gap-1 min-w-max">
              {nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  className={({ isActive }) =>
                    `flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium whitespace-nowrap ${
                      isActive ? "bg-primary text-primary-foreground" : "bg-muted text-foreground/70"
                    }`
                  }
                >
                  <n.icon className="h-3.5 w-3.5" />
                  {n.label}
                </NavLink>
              ))}
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto en-scroll">
          <div className="p-6 lg:p-10 max-w-7xl mx-auto w-full">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
